import { unflattenObject, flattenObject } from 'smartdoors-shared/js/helpers/common';
import objectStores from './db/stores';

class DbStorage {
    constructor(objectStores, version = 1) {
        this.error = null;
        this.version = version;
        this.objectStores = objectStores;
        this.db = null;

        this.ready = new Promise((resolve, reject) => {
            const dbRequest = indexedDB.open('smartdoors', version);
            dbRequest.onupgradeneeded = e => { this.upgradeDb(e); };

            dbRequest.onsuccess = e => {
                this.db = e.target.result;
                resolve(this.db);
            };

            dbRequest.onerror = e => {
                this.error = e.target.errorCode;
                reject(e);
            };
        });
    }

    upgradeDb(upgradeNeededEvent) {
        const db = upgradeNeededEvent.target.result;

        if (upgradeNeededEvent.newVersion in this.objectStores) {
            const newStores = this.objectStores[upgradeNeededEvent.newVersion];

            if (upgradeNeededEvent.oldVersion in this.objectStores) {
                const oldStores = this.objectStores[upgradeNeededEvent.oldVersion];

                Object.keys(oldStores).forEach(key => {
                    db.deleteObjectStore(key);
                });
            }

            Object.entries(newStores).forEach(([key, value]) => {
                let objectStore = null;

                if ('createOptions' in value) {
                    objectStore = db.createObjectStore(key, value.createOptions);
                } else {
                    objectStore = db.createObjectStore(key);
                }

                if ('indexes' in value) {
                    Object.keys(value.indexes).forEach(indexName => {
                        const index = value.indexes[indexName];

                        objectStore.createIndex(
                            indexName,
                            ('keyPath' in index ? index.keyPath : indexName),
                            ('options' in index ? index.options : {})
                        );
                    });
                }
            })
        }
    }

    getObjectStore(store, mode = 'readonly') {
        if (typeof store != 'string') {
            return store;
        }

        const transaction = this.db.transaction([store], mode);

        return transaction.objectStore(store);
    }

    async setItem(key, data) {
        const objectStore = this.getObjectStore(key, 'readwrite');

        objectStore.put(data, key);
    }

    async setList(key, data) {
        const objectStore = this.getObjectStore(key, 'readwrite');

        data.forEach(item => {
            objectStore.put(item);
        });
    }

    async syncList(key, data) {
        const objectStore = this.getObjectStore(key, 'readwrite');

        objectStore.openCursor().onsuccess = e => {
            const cursor = e.target.result;

            if (cursor) {
                if (!data.find(p => p[cursor.source.keyPath] == cursor.key)) {
                    objectStore.delete(cursor.key);
                }
                cursor.continue();
            }
        };

        data.forEach(item => {
            objectStore.put(item);
        });
    }

    setFlattened(key, data) {
        const objectStore = this.getObjectStore(key, 'readwrite');
        const dataFlattened = flattenObject(data);

        Object.keys(dataFlattened).forEach(key => {
            const keyData = dataFlattened[key];

            if (keyData === undefined) {
                objectStore.delete(key);
            } else {
                objectStore.put(keyData, key);
            }
        });
    }

    async getItem(store, itemKey) {
        return new Promise((resolve, reject) => {
            this.getObjectStore(store).get(itemKey).onsuccess = e => {
                resolve(e.target.result);
            };
        });
    }

    async getItems(store, itemKeys) {
        const objectStore = this.getObjectStore(store);
        const getRequests = [];

        for (const itemKey of itemKeys) {
            getRequests.push(this.getItem(objectStore, itemKey));
        }

        return Promise.all(getRequests);
    }

    async deleteItem(store, itemKey) {
        return new Promise((resolve, reject) => {
            this.getObjectStore(store, 'readwrite').delete(itemKey).onsuccess = e => {
                resolve(e);
            };
        });
    }

    async deleteItems(store, itemKeys) {
        const objectStore = this.getObjectStore(store, 'readwrite');
        const deleteRequests = [];

        for (const itemKey of itemKeys) {
            deleteRequests.push(this.deleteItem(objectStore, itemKey));
        }

        return Promise.all(deleteRequests);
    }

    async getList(key) {
        return new Promise((resolve, reject) => {
            const objectStore = this.getObjectStore(key);
            const list = [];

            objectStore.openCursor().onsuccess = e => {
                const cursor = e.target.result;

                if (cursor) {
                    list.push(cursor.value);
                    cursor.continue();
                } else {
                    resolve(list);
                }
            };
        });
    }

    async findInList(store, filter = {}, searchString = null, searchProps = null) {
        const objectStore = this.getObjectStore(store);
        const storeMeta = this.getStoreMeta(store);

        const filterKeys = Object.keys(filter);
        const filterValues = Object.values(filter);
        const additionalChecks = {};

        // parse filters
        /*Object.entries(filter).forEach(([ key, value ]) => {
            if(Array.isArray(value)) {
                // if the value is an array it should be an OR operation - we can do this only later with manual additional checks on the result
                additionalChecks[key] = value;
            } else {
                filterKeys.push(key);
                filterValues.push(value);
            }
        });*/

        let result = null;

        if (filterKeys.length) {
            // try use indexes to get the first result if there are filter keys set
            try {
                result = await new Promise(async (resolve, reject) => {
                    const filterIndexCompound = filterKeys.join('.');
                    const filtersHaveOrConditions = filterValues.some(p => Array.isArray(p));

                    if (!filtersHaveOrConditions && 'indexes' in storeMeta && filterIndexCompound in storeMeta.indexes) {
                        // If there is a compound index for the given filter keys and there ar no OR-conditions we just can get all items for the compound index  
                        const items = [];

                        objectStore.index(filterIndexCompound).openCursor(filterValues.length > 1 ? filterValues : filterValues[0]).onsuccess = e => {
                            const cursor = e.target.result;

                            if (cursor) {
                                items.push(cursor.value);
                                cursor.continue();
                            } else {
                                resolve(items);
                            }
                        };
                    } else if ('indexes' in storeMeta) {
                        // if there is no matching compound index or we have multiple values in at least one filter (= need OR conditions)
                        // .. we have to get the keys for each value and get the items for the combined keys
                        const getKeys = [];
                        let foundKeys = null;

                        for (const index of filterKeys) {
                            if (!(index in storeMeta.indexes)) {
                                reject(`No index set for filter key ${index}`);
                                return false;
                            }

                            const getValKeys = [];
                            const indexValues = Array.isArray(filter[index]) ? filter[index] : [filter[index]];

                            for (const val of indexValues) {
                                // get keys for each value (= OR condition if there multiple values for one key)
                                getValKeys.push(new Promise((rslv, rjct) => {
                                    const getKeysRequest = objectStore.index(index).getAllKeys(val);

                                    getKeysRequest.onsuccess = () => {
                                        rslv(getKeysRequest.result);
                                    };
                                }));
                            }

                            getKeys.push(Promise.all(getValKeys).then(valKeysResults => {
                                // when all keys for all filter values got received we reduce it to one keys array without duplicates
                                let valKeys = [];

                                for (const valKeyResult of valKeysResults) {
                                    valKeys = valKeys.concat(valKeyResult.filter(key => !valKeys.includes(key)))
                                }

                                // .. and add it to the final foundKeys array (now using AND-condition)
                                if (!foundKeys) {
                                    foundKeys = valKeys;
                                } else {
                                    foundKeys = foundKeys.filter(p => valKeys.includes(p));
                                }
                            }));
                        }

                        await Promise.all(getKeys);

                        resolve(await this.getItems(store, foundKeys));
                    } else {
                        reject('No indexes are set for filtering');
                    }
                });
            } catch (error) {
                console.warn('Warning, could not request result with indexes: ' + error);
            }
        }

        if (result === null) {
            // if we haven't got a result yet, we'll request all entries
            result = await new Promise((resolve, reject) => {
                const getAllRequest = objectStore.getAll();

                getAllRequest.onsuccess = () => {
                    resolve(getAllRequest.result);
                };
            });
        }


        // apply search and additional checks on the result
        const additionalChecksEntries = Object.entries(additionalChecks);

        if (result.length && (additionalChecksEntries.length || (searchProps && searchString))) {
            const newResult = [];

            if (!Array.isArray(searchProps)) {
                searchProps = searchProps ? [searchProps] : [];
            }

            for (const item of result) {
                let useItem = true;

                if (additionalChecksEntries.length) {
                    additionalChecksEntries.forEach(([key, value]) => {
                        if (Array.isArray(value)) {
                            useItem = useItem && value.some(p => p == item[key]);
                        } else {
                            useItem = useItem && value == item[key];
                        }
                    });
                }

                if (useItem && searchString && searchProps.length) {
                    useItem = false;
                    searchString = searchString.toLowerCase();

                    loopSearchProps:
                    for (const prop of searchProps) {
                        let propData = item;
                        let propParts = prop.split('.');

                        for (const propPart of propParts) {
                            if (propPart in propData) {
                                propData = propData[propPart];
                            } else {
                                break loopSearchProps;
                            }
                        }

                        if (propData.toString().toLowerCase().indexOf(searchString) != -1) {
                            useItem = true;
                            break;
                        }
                    }
                }

                if (useItem) {
                    newResult.push(item);
                }
            }

            return newResult;
        }


        return result;
    }

    async getFlattened(key) {
        return new Promise((resolve, reject) => {
            const objectStore = this.getObjectStore(key);
            const data = {};

            objectStore.openCursor().onsuccess = e => {
                const cursor = e.target.result;

                if (cursor) {
                    data[cursor.key] = cursor.value;
                    cursor.continue();
                } else {
                    resolve(unflattenObject(data));
                }
            };
        });
    }

    getStores() {
        return this.objectStores[this.version];
    }

    getStoreMeta(key) {
        return key in this.objectStores[this.version] ? this.objectStores[this.version][key] : null;
    }
}



export default new DbStorage(objectStores, 2);